import { Fragment, isValidElement } from "react"
import type { ComponentProps, FC, ReactElement, ReactNode } from "react"

import eq from "lodash-es/eq"
import isArray from "lodash-es/isArray"
import isEmpty from "lodash-es/isEmpty"
import isString from "lodash-es/isString"
import { Link as RouterLink } from "react-router-dom"

import { buildCssClassName, getTransitionTwClasses } from "@/3514/utils"

import {
    ETypographyColor,
    ETypographyFamily,
    ETypographySize,
    ETypographyTag,
    ETypographyWeight,
    Typography
} from "../typography"

type TProps = ComponentProps<typeof RouterLink> & {
    children: string | ReactNode | ReactNode[]
    className?: string
    textClassName?: string // for nested children
    isTargetBlank?: boolean
    textSize?: ETypographySize
    textColor?: ETypographyColor
    textWeight?: ETypographyWeight
    textFamily?: ETypographyFamily
    styleless?: boolean
}

const classes = {
    link: (): string => "group focus:!outline-[0px] inline-flex items-center",
    content: ({ color }: { color: ETypographyColor }): string =>
        buildCssClassName(
            [
                [eq(color, ETypographyColor.Accent) && "decoration-accent"],
                [eq(color, ETypographyColor.Blue200) && "decoration-blue-200"],
                "decoration-[1px]",
                "underline",
                "underline-offset-[3px]",
                "group-focus:underline-offset-[5px]",
                "group-hover:underline-offset-[5px]"
            ],
            getTransitionTwClasses()
        )
} as const

function renderLabel(
    family: ETypographyFamily,
    color: ETypographyColor,
    weight: ETypographyWeight,
    size: ETypographySize,
    className: string,
    children: ReactNode,
    styleless: boolean
): ReactNode {
    return (
        <Typography
            tag={ETypographyTag.Span}
            className={buildCssClassName(styleless ? [] : [classes.content({ color }), { [className]: className }])}
            color={color}
            weight={weight}
            size={size}
            text={children}
            family={family}
        />
    )
}

const Component: FC<TProps> = ({
    to,
    children,
    isTargetBlank = false,
    className = String(),
    textClassName = String(""),
    textSize = ETypographySize.Small,
    textColor = ETypographyColor.Accent,
    textWeight = ETypographyWeight.Bold,
    textFamily = ETypographyFamily.Nunito,
    styleless = false,
    ...props
}: TProps): ReactElement => (
    <RouterLink
        to={to}
        {...(isTargetBlank && { target: "_blank" })}
        {...props}
        className={buildCssClassName(classes.link(), { [className]: className })}
    >
        {isArray(children) ? (
            <Fragment>
                {children[0]}
                {isEmpty(children?.[1])
                    ? null
                    : isString(children[1])
                      ? renderLabel(textFamily, textColor, textWeight, textSize, textClassName, children[1], styleless)
                      : isValidElement(children[1])
                        ? children[1]
                        : null}
            </Fragment>
        ) : isString(children) ? (
            renderLabel(textFamily, textColor, textWeight, textSize, textClassName, children, styleless)
        ) : (
            children
        )}
    </RouterLink>
)

Component.displayName = "Link"

export { Component as Link }
